import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Compact info icon for inline help next to labels/section titles.
 * Opens a positioned popover on click (touch-friendly) and on hover/focus (pointer fine).
 */
const InfoHintButton = ({
  text,
  ariaLabel,
  className = '',
  onClick,
  maxWidth = 280,
}) => {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const anchorRef = useRef(null);
  const popoverRef = useRef(null);
  const [popoverStyle, setPopoverStyle] = useState({});

  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return;

    const rect = anchorRef.current.getBoundingClientRect();
    const width = Math.min(maxWidth, window.innerWidth - 16);
    let top = rect.bottom + 8;
    let left = rect.left + rect.width / 2 - width / 2;

    if (left < 8) left = 8;
    if (left + width > window.innerWidth - 8) {
      left = window.innerWidth - width - 8;
    }

    const popoverHeight = popoverRef.current?.offsetHeight ?? 96;
    if (top + popoverHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - popoverHeight - 8);
    }

    setPopoverStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      zIndex: 99999,
    });
  }, [maxWidth]);

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (
        anchorRef.current?.contains(event.target) ||
        popoverRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
      setPinned(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setPinned(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  useEffect(() => {
    if (!open || !popoverRef.current) return undefined;

    updatePosition();
    return undefined;
  }, [open, text, updatePosition]);

  if (!text) return null;

  const supportsHover =
    typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const handleClick = (event) => {
    event.stopPropagation();
    onClick?.(event);
    setOpen((wasOpen) => {
      const nextOpen = !wasOpen;
      setPinned(nextOpen);
      return nextOpen;
    });
  };

  const handleMouseEnter = () => {
    if (supportsHover && !pinned) {
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (supportsHover && !pinned) {
      setOpen(false);
    }
  };

  const handleFocus = () => {
    if (!pinned) setOpen(true);
  };

  const handleBlur = (event) => {
    if (pinned) return;
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setOpen(false);
    }
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-text-muted transition hover:bg-surface-subtle hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${className}`.trim()}
        aria-label={ariaLabel || 'More information'}
        aria-expanded={open}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            role="tooltip"
            className="rounded-md border border-border-medium bg-surface px-3 py-2 text-left text-xs leading-relaxed text-text-primary shadow-lg"
            style={popoverStyle}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  );
};

export default InfoHintButton;
